// attributeEnvelope — the workspace-attribute write rule, ported byte-exact from
// `Orphex-New-Front/src/stores/workspaceStore.ts` (`setAttributes` :531-543, `normalizeValues`
// :570-579, `getBody` :580-585). Contract 22 §6.1 tabulates every row; this module is the only
// place that knows them, and 30 + 23 import it rather than forking it.
//
// The rule is asymmetric and non-obvious on purpose — it is legacy logic that is BETTER than what
// a rewrite would produce, so it is ported rather than cleaned up. Two traps:
//
//   1. The URL branches on plain TRUTHINESS of the value; the body branches on `hasValue`, a
//      DIFFERENT predicate (an empty array is truthy but not `hasValue`). That gap is the
//      "clear the list" path: `update/` with a body that carries no `value` at all.
//   2. `create` is shaped nothing like `update` — the attribute key travels as `type` and the
//      value nests under `body`. Collapsing all three verbs onto a symmetric `{id, key, value}`
//      is the single easiest way to get this module silently wrong.
//
// `toRaw` is the one import here. It is a pure value function (no component, store, reactivity
// context or HTTP), so the module stays synchronously testable with no app mounted — which is what
// §4's "service/** imports no Vue" is protecting. Legacy guards it with `isProxy(v) ? toRaw(v) : v`;
// `toRaw` already returns a non-proxy unchanged, so the guard is dropped as redundant, not as a
// behaviour change.
import { toRaw } from 'vue'

import type { WorkspaceAttributeId } from '../models/attribute.types'

export type AttributeVerb = 'create' | 'update' | 'delete' | 'noop'
/** The three verbs that actually send a request. `noop` never reaches `attributeBody`. */
export type AttributeWriteVerb = Exclude<AttributeVerb, 'noop'>

/** The BODY predicate (`getBody` :581) — deliberately not the verb predicate. An empty array is
 *  "no value" here, while the verb rule counts it as a value. */
function hasValue(value: unknown): boolean {
  return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null
}

/** Unwrap a Vue proxy before the value goes on the wire (`normalizeValues` :570-579). A falsy
 *  value normalises to `null` — that is what turns a `delete`'s body into a bare `{key_id}`.
 *  Legacy's `Array.isArray(raw) && raw.length === 0` branch is dead (`Object.keys([])` is always
 *  empty, so it can never return anything) and is dropped (D12). */
export function normalizeValues(values: unknown): unknown {
  if (!values) return null
  return toRaw(values)
}

/** Which endpoint a write targets (`setAttributes` :533-539). Note it keys on plain truthiness of
 *  `value`, NOT on `hasValue`:
 *
 *  | `id`   | `value`                          | verb     |
 *  | ------ | -------------------------------- | -------- |
 *  | truthy | truthy (**including `[]`/`{}`**) | `update` |
 *  | truthy | falsy                            | `delete` |
 *  | falsy  | truthy, and `hasValue`           | `create` |
 *  | falsy  | falsy                            | `noop`   |
 *  | falsy  | `[]`                             | `noop` ⚠ |
 *
 *  ⚠ The last row is the one deliberate divergence (§14). Legacy would `create/` with an empty
 *  body `{}` — there is nothing to create, so v4 sends no request at all. */
export function attributeVerb(id: WorkspaceAttributeId, value: unknown): AttributeVerb {
  if (id) return value ? 'update' : 'delete'
  if (!value) return 'noop'
  return hasValue(value) ? 'create' : 'noop'
}

/** The request body for a write (`getBody` :580-585):
 *
 *  | verb                       | body                                      |
 *  | -------------------------- | ----------------------------------------- |
 *  | `update` (has a value)     | `{ key_id, value }`                       |
 *  | `update` (value is `[]`)   | `{ key_id }` — ⚠ no `value` field         |
 *  | `delete`                   | `{ key_id }`                              |
 *  | `create`                   | `{ key: true, type: <key>, body: {value}}` |
 *
 *  `update` and `delete` share one branch exactly as legacy does: a `delete` only ever arrives
 *  with a falsy value, which normalises to `null` and so never satisfies `hasValue`. */
export function attributeBody(
  verb: AttributeWriteVerb,
  id: WorkspaceAttributeId,
  key: string,
  value: unknown,
): Record<string, unknown> {
  const normalized = normalizeValues(value)

  if (verb === 'create') return { key: true, type: key, body: { value: normalized } }

  return hasValue(normalized) ? { key_id: id, value: normalized } : { key_id: id }
}
